/**
 * Generates labelled demo curves for the customer-app energy dashboard.
 * No real P1 data — every screen using this MUST display the
 * "Voorbeeldweergave — koppel uw P1-meter voor echte data" badge.
 *
 * All values are illustrative kWh / EUR figures, NOT savings claims.
 *
 * Values are CACHED per period for the lifetime of the page session so
 * Home and Inzicht tabs display the same numbers.
 */

const _curveCache = {};
let _totalsCache = null;

function shape(period) {
  // [{ label, verbruik, teruglevering }]
  if (period === 'Nu') {
    // last 60 minutes, 5-min buckets
    return Array.from({ length: 12 }).map((_, i) => {
      const min = i * 5;
      const v = 0.25 + 0.08 * Math.sin(i / 2) + (i % 3 === 0 ? 0.12 : 0);
      const t = i > 6 ? 0.05 + 0.04 * Math.sin(i) : 0;
      return { label: `${String(min).padStart(2, '0')}m`, verbruik: +v.toFixed(2), teruglevering: +t.toFixed(2) };
    });
  }
  if (period === 'Dag') {
    // 24 hours
    return Array.from({ length: 24 }).map((_, h) => {
      // morning + evening peaks, solar export midday
      const peakMorning = h >= 7 && h <= 9 ? 0.9 + Math.random() * 0.3 : 0;
      const peakEvening = h >= 17 && h <= 21 ? 1.1 + Math.random() * 0.4 : 0;
      const base = 0.25 + 0.1 * Math.sin(h / 4);
      const verbruik = +(base + peakMorning + peakEvening).toFixed(2);
      const solar = h >= 9 && h <= 17
        ? Math.max(0, 1.4 * Math.sin(((h - 9) / 8) * Math.PI))
        : 0;
      const teruglevering = +Math.max(0, solar - verbruik * 0.6).toFixed(2);
      return { label: `${String(h).padStart(2, '0')}u`, verbruik, teruglevering };
    });
  }
  if (period === 'Week') {
    const days = ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'];
    return days.map((d, i) => ({
      label: d,
      verbruik: +(11 + Math.sin(i) * 2 + Math.random() * 1.5).toFixed(1),
      teruglevering: +(5 + Math.cos(i) * 1.5 + Math.random()).toFixed(1),
    }));
  }
  if (period === 'Maand') {
    return Array.from({ length: 30 }).map((_, i) => ({
      label: `${i + 1}`,
      verbruik: +(10 + 3 * Math.sin(i / 3) + Math.random() * 2).toFixed(1),
      teruglevering: +(4 + 2 * Math.cos(i / 4) + Math.random()).toFixed(1),
    }));
  }
  // Jaar
  const months = ['Jan', 'Feb', 'Mrt', 'Apr', 'Mei', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'];
  return months.map((m, i) => {
    // higher consumption in winter, more solar in summer
    const winter = Math.cos((i / 12) * 2 * Math.PI);
    return {
      label: m,
      verbruik: +(280 + 120 * winter + Math.random() * 20).toFixed(0),
      teruglevering: +(80 - 60 * winter + Math.random() * 15).toFixed(0),
    };
  });
}

export function getDemoCurve(period) {
  if (!_curveCache[period]) _curveCache[period] = shape(period);
  return _curveCache[period];
}

export function getDemoTodayTotals() {
  if (_totalsCache) return _totalsCache;
  const day = getDemoCurve('Dag');
  const verbruik = day.reduce((s, r) => s + r.verbruik, 0);
  const teruglevering = day.reduce((s, r) => s + r.teruglevering, 0);
  const eigen = Math.max(0, verbruik - teruglevering * 0.4); // illustrative
  // Indicative price band (EUR), not a claim
  const priceKwh = 0.32; // illustrative tariff
  const feedinKwh = 0.08;
  const kostenIndicatie = verbruik * priceKwh - teruglevering * feedinKwh;
  _totalsCache = {
    verbruik_kwh: +verbruik.toFixed(1),
    teruglevering_kwh: +teruglevering.toFixed(1),
    eigen_zon_kwh: +eigen.toFixed(1),
    kosten_euro: +kostenIndicatie.toFixed(2),
  };
  return _totalsCache;
}
